import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  sortableContainer,
  sortableElement,
  arrayMove,
} from 'react-sortable-hoc';
import {
  invoiceDocumentPreferenceDocTypes,
  invoiceDocumentPreferenceDocTypesMap,
} from 'config/documents';
import SessionStore from 'stores/SessionStore';
import Checkbox from 'components/v2/forms/Checkbox';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';

const SortableItem = sortableElement(({ children }) => (
  <tr className="cursor-pointer">{children}</tr>
));
const SortableContainer = sortableContainer(({ children }) => {
  return <tbody>{children}</tbody>;
});

function InvoiceDocumentPreferencesForm({
  currentInvoiceDocumentPreferences,
  onSave,
  onCancel,
  t,
}) {
  const [invoiceDocumentPreferences, setInvoiceDocumentPreferences] = useState(
    [],
  );

  function saveDisabled() {
    return !SessionStore.hasFinanceRole();
  }

  function handleSave() {
    onSave(invoiceDocumentPreferences);
  }

  useEffect(() => {
    setInvoiceDocumentPreferences(currentInvoiceDocumentPreferences);
  }, [JSON.stringify(currentInvoiceDocumentPreferences)]);

  function addDocumenType(slug) {
    const sequence = invoiceDocumentPreferences.length + 1;
    const updatedPreferences = [
      ...invoiceDocumentPreferences,
      { slug, sequence },
    ];
    setInvoiceDocumentPreferences(updatedPreferences);
  }

  function removeDocumenType(slug) {
    const updatedPreferences = invoiceDocumentPreferences
      .filter((pref) => pref.slug !== slug)
      .map((pref, i) => ({ ...pref, sequence: i + 1 }));
    setInvoiceDocumentPreferences(updatedPreferences);
  }

  function handleRemoveDocumenType(slug) {
    return function () {
      removeDocumenType(slug);
    };
  }

  function handleToggleDocumentType(slug) {
    return function (checked) {
      if (!checked) {
        removeDocumenType(slug);
      } else {
        addDocumenType(slug);
      }
    };
  }

  function isChecked(slug) {
    return !!invoiceDocumentPreferences.find((pref) => pref.slug === slug);
  }

  function isDisabled(type) {
    if (!SessionStore.hasFinanceRole()) {
      return true;
    }
    return invoiceDocumentPreferenceDocTypesMap?.[type]?.requiredPreference;
  }

  function handleSortEnd({ oldIndex, newIndex }) {
    const updatedPreferences = arrayMove(
      invoiceDocumentPreferences,
      oldIndex,
      newIndex,
    ).map((pref, i) => {
      const { slug } = pref;
      const sequence = i + 1;
      return {
        slug,
        sequence,
      };
    });
    setInvoiceDocumentPreferences(updatedPreferences);
  }

  return (
    <div>
      <h4 className="my-0 mb-1">{t('DOCUMENT_TYPES')}</h4>
      <p className="text-sm mb-4">{t('PLEASE_SELECT_SEQ_BELOW')}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {invoiceDocumentPreferenceDocTypes.map((docType) => (
          <Checkbox
            key={docType.value}
            checked={isChecked(docType.value)}
            disabled={isDisabled(docType.value)}
            label={t(docType.localizationSlug, { count: docType.count })}
            onChange={handleToggleDocumentType(docType.value)}
            id={docType.value}
          />
        ))}
      </div>
      <hr className="my-4" />
      <div className="overflow-x-auto mt-4 overyflow-y-auto">
        <table className="default-table">
          <thead>
            <tr>
              <th>{t('DOCUMENT')}</th>
              <th>{t('ACTIONS')}</th>
            </tr>
          </thead>
          <SortableContainer onSortEnd={handleSortEnd} distance={10}>
            {invoiceDocumentPreferences.map((pref, i) => {
              const { slug, sequence } = pref;
              const key = `${slug}-${sequence}`;
              const docType = invoiceDocumentPreferenceDocTypesMap[slug];
              return (
                <SortableItem key={key} index={i} disabled={saveDisabled()}>
                  <td>
                    <div className="flex flex-wrap items-center space-x-2">
                      <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 inline-flex items-center justify-center">
                        <span>{sequence}</span>
                      </div>
                      <span className="flex-1 text-sm text-gray-800">
                        {t(docType?.localizationSlug)}
                      </span>
                    </div>
                  </td>
                  <td>
                    {!docType?.requiredPreference &&
                      SessionStore.hasFinanceRole() && (
                        <DeleteButton
                          onClick={handleRemoveDocumenType(slug)}
                          size="small"
                        />
                      )}
                  </td>
                </SortableItem>
              );
            })}
          </SortableContainer>
        </table>
      </div>
      <div className="flex items-center justify-end mt-4 space-x-2">
        <button
          type="button"
          onClick={handleSave}
          className="button button-green"
          disabled={saveDisabled()}
        >
          {titleize(t('SAVE'))}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="button button-seconary"
        >
          {t('CANCEL')}
        </button>
      </div>
    </div>
  );
}

InvoiceDocumentPreferencesForm.propTypes = {
  currentInvoiceDocumentPreferences: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      sequence: PropTypes.number,
    }),
  ),
};

InvoiceDocumentPreferencesForm.defaultProps = {
  currentInvoiceDocumentPreferences: [],
};

export default withTranslation()(InvoiceDocumentPreferencesForm);
